export type BasicInfoFields = {
  year: string;
  month: string;
  dobY: string;
  dobM: string;
  dobD: string;
};

export const BASIC_INFO_LOCAL_KEY = 'azure-memoapp:basic-info-v1';

/** マウント直後の空フォームで localStorage を消さないためのフラグ */
let basicInfoHydrated = false;

export function isBasicInfoHydrated(): boolean {
  return basicInfoHydrated;
}

export function markBasicInfoHydrated(): void {
  basicInfoHydrated = true;
}

function field(id: string): HTMLInputElement | HTMLSelectElement | null {
  return document.querySelector(id);
}

function toHalfWidthDigits(raw: string, maxLen: number): string {
  return String(raw ?? '').replace(/[^0-9]/g, '').slice(0, maxLen);
}

function normalizeFields(raw: Partial<BasicInfoFields> | null | undefined): BasicInfoFields {
  return {
    year: toHalfWidthDigits(String(raw?.year ?? ''), 4),
    month: toHalfWidthDigits(String(raw?.month ?? ''), 2),
    dobY: toHalfWidthDigits(String(raw?.dobY ?? ''), 4),
    dobM: toHalfWidthDigits(String(raw?.dobM ?? ''), 2),
    dobD: toHalfWidthDigits(String(raw?.dobD ?? ''), 2)
  };
}

function isEmptyFields(fields: BasicInfoFields): boolean {
  return !fields.year && !fields.month && !fields.dobY && !fields.dobM && !fields.dobD;
}

function setSelectValue(el: HTMLSelectElement | null, value: string): void {
  if (!el) return;
  const v = value || '';
  if (v && !Array.from(el.options).some((o) => o.value === v)) {
    const opt = document.createElement('option');
    opt.value = v;
    opt.textContent = v;
    el.appendChild(opt);
  }
  el.value = v;
}

function fillSelectRange(
  el: HTMLSelectElement | null,
  start: number,
  end: number,
  blankLabel: string,
  descending = false
): void {
  if (!el) return;
  const keep = el.value;
  el.innerHTML = '';
  const blank = document.createElement('option');
  blank.value = '';
  blank.textContent = blankLabel;
  el.appendChild(blank);
  if (descending) {
    for (let n = end; n >= start; n -= 1) {
      const opt = document.createElement('option');
      opt.value = String(n);
      opt.textContent = String(n);
      el.appendChild(opt);
    }
  } else {
    for (let n = start; n <= end; n += 1) {
      const opt = document.createElement('option');
      opt.value = String(n);
      opt.textContent = String(n);
      el.appendChild(opt);
    }
  }
  if (keep) setSelectValue(el, keep);
}

function daysInMonth(year: number, month: number): number {
  if (!year || !month) return 31;
  return new Date(year, month, 0).getDate();
}

/** サイドバーの select 選択肢を埋める（マウント時に1回） */
export function populateBasicInfoSelects(): void {
  const nowY = new Date().getFullYear();
  fillSelectRange(field('#basic-info-year') as HTMLSelectElement | null, nowY - 2, nowY + 5, '—');
  fillSelectRange(field('#basic-info-month') as HTMLSelectElement | null, 1, 12, '—');
  fillSelectRange(
    field('#basic-info-dob-y') as HTMLSelectElement | null,
    1940,
    nowY,
    '年',
    true
  );
  fillSelectRange(field('#basic-info-dob-m') as HTMLSelectElement | null, 1, 12, '月');
  refreshBasicInfoDobDays();
}

/** 生年月日の日選択肢を年月に合わせて更新 */
export function refreshBasicInfoDobDays(): void {
  const y = Number(toHalfWidthDigits(field('#basic-info-dob-y')?.value ?? '', 4));
  const m = Number(toHalfWidthDigits(field('#basic-info-dob-m')?.value ?? '', 2));
  const maxDay = daysInMonth(y, m);
  const dayEl = field('#basic-info-dob-d') as HTMLSelectElement | null;
  const keep = dayEl?.value ?? '';
  fillSelectRange(dayEl, 1, maxDay, '日');
  if (keep && Number(keep) <= maxDay) setSelectValue(dayEl, keep);
  else if (dayEl) dayEl.value = '';
}

export function readBasicInfoFromForm(): BasicInfoFields {
  return {
    year: toHalfWidthDigits(field('#basic-info-year')?.value ?? '', 4),
    month: toHalfWidthDigits(field('#basic-info-month')?.value ?? '', 2),
    dobY: toHalfWidthDigits(field('#basic-info-dob-y')?.value ?? '', 4),
    dobM: toHalfWidthDigits(field('#basic-info-dob-m')?.value ?? '', 2),
    dobD: toHalfWidthDigits(field('#basic-info-dob-d')?.value ?? '', 2)
  };
}

export function applyBasicInfoToForm(data: BasicInfoFields): void {
  const norm = normalizeFields(data);
  setSelectValue(field('#basic-info-year') as HTMLSelectElement | null, norm.year);
  setSelectValue(field('#basic-info-month') as HTMLSelectElement | null, norm.month);
  setSelectValue(field('#basic-info-dob-y') as HTMLSelectElement | null, norm.dobY);
  setSelectValue(field('#basic-info-dob-m') as HTMLSelectElement | null, norm.dobM);
  refreshBasicInfoDobDays();
  setSelectValue(field('#basic-info-dob-d') as HTMLSelectElement | null, norm.dobD);
}

export function clearBasicInfoForm(): void {
  applyBasicInfoToForm({ year: '', month: '', dobY: '', dobM: '', dobD: '' });
}

export function loadBasicInfoFromLocal(): BasicInfoFields | null {
  try {
    const raw = localStorage.getItem(BASIC_INFO_LOCAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<BasicInfoFields>;
    const data = normalizeFields(parsed);
    return isEmptyFields(data) ? null : data;
  } catch {
    return null;
  }
}

export function saveBasicInfoToLocal(
  fields: BasicInfoFields
): { ok: true; cleared?: boolean } | { ok: false; message: string } {
  try {
    const data = normalizeFields(fields);
    if (isEmptyFields(data)) {
      localStorage.removeItem(BASIC_INFO_LOCAL_KEY);
      return { ok: true, cleared: true };
    }
    localStorage.setItem(BASIC_INFO_LOCAL_KEY, JSON.stringify(data));
    return { ok: true };
  } catch {
    return { ok: false, message: 'この端末への保存に失敗しました。' };
  }
}

/** フォーム内容を localStorage に書き込む（全画面共通の単一ソース） */
export function persistBasicInfoFormToLocal(): { ok: true; cleared?: boolean } | { ok: false; message: string } {
  // 復元前の空フォームで消さない（リロード直後のレース対策）
  if (!basicInfoHydrated) return { ok: true };
  return saveBasicInfoToLocal(readBasicInfoFromForm());
}

/**
 * マウント直後に localStorage から同期復元する。
 * 認証待ちの前に呼ぶこと（Tax 計算より先に値が入るようにする）。
 */
export function hydrateBasicInfoFromLocal(): BasicInfoFields | null {
  const local = loadBasicInfoFromLocal();
  if (local) applyBasicInfoToForm(local);
  markBasicInfoHydrated();
  return local;
}

async function loadBasicInfoFromServer(): Promise<BasicInfoFields | null> {
  try {
    const res = await fetch('/api/basic-info/me', { credentials: 'include', cache: 'no-store' });
    if (!res.ok) return null;
    const j = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      year?: string;
      month?: string;
      dobY?: string;
      dobM?: string;
      dobD?: string;
    };
    if (!j.ok) return null;
    const data = normalizeFields({
      year: String(j.year ?? ''),
      month: String(j.month ?? ''),
      dobY: String(j.dobY ?? ''),
      dobM: String(j.dobM ?? ''),
      dobD: String(j.dobD ?? '')
    });
    return isEmptyFields(data) ? null : data;
  } catch {
    return null;
  }
}

/**
 * 起動時の復元。localStorage を優先（画面間共有の単一ソース）。
 * 空のときだけ、認証済みならサーバーから拾って local にも書く。
 * ※ 通常は hydrateBasicInfoFromLocal() を先に呼んでおく。
 */
export async function loadBasicInfoForSession(isAuthed: boolean): Promise<BasicInfoFields | null> {
  const local = loadBasicInfoFromLocal();
  if (local) {
    markBasicInfoHydrated();
    return local;
  }
  if (!isAuthed) {
    markBasicInfoHydrated();
    return null;
  }
  const fromServer = await loadBasicInfoFromServer();
  markBasicInfoHydrated();
  if (!fromServer) return null;
  saveBasicInfoToLocal(fromServer);
  return fromServer;
}
